import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Module-level "Contexte de travail" — defines the scope NOAM uses when
 * generating module-specific analyses (commentaries, fractale, brief, etc.).
 *
 * Scopes:
 *  - { type: "self" }           → personal profile of the current user
 *  - { type: "org" }            → the active organisation (default)
 *  - { type: "project", id }    → a specific in-org project / opportunity / risk
 *  - { type: "external", name } → free-text external counterparty/org
 *
 * Persisted in localStorage under `noam-context-{module}` so each module
 * remembers its scope across reloads. Exposed via a React Context so any
 * AI-calling component can read it and forward to the backend.
 */

const STORAGE_PREFIX = "noam-context-";
const DEFAULT_SCOPE = { type: "org", label: "Mon organisation" };

function readScope(moduleKey) {
  if (typeof window === "undefined") return DEFAULT_SCOPE;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + moduleKey);
    if (!raw) return DEFAULT_SCOPE;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.type) return parsed;
  } catch {
    /* silent — defensive */
  }
  return DEFAULT_SCOPE;
}

function writeScope(moduleKey, scope) {
  try { window.localStorage.setItem(STORAGE_PREFIX + moduleKey, JSON.stringify(scope)); }
  catch { /* silent — defensive */ }
}

const ModuleContext = createContext({ scope: DEFAULT_SCOPE, setScope: () => {} });

export function ModuleContextProvider({ moduleKey, children }) {
  const [scope, setScopeState] = useState(() => readScope(moduleKey));
  useEffect(() => { setScopeState(readScope(moduleKey)); }, [moduleKey]);
  const setScope = useCallback((next) => {
    setScopeState(next);
    writeScope(moduleKey, next);
    try {
      window.dispatchEvent(new CustomEvent("noam:context-changed", {
        detail: { moduleKey, scope: next },
      }));
    } catch { /* silent — defensive */ }
  }, [moduleKey]);
  return (
    <ModuleContext.Provider value={{ scope, setScope }}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModuleContext() {
  return useContext(ModuleContext);
}

/**
 * Lightweight hook for components that aren't wrapped in a ModuleContextProvider
 * (e.g. existing pages mid-migration). Returns a read-only scope + a setter
 * that persists to localStorage. The hook listens to `noam:context-changed`
 * events so all consumers stay in sync.
 */
export function useLocalContext(moduleKey) {
  const [scope, setScopeState] = useState(() => readScope(moduleKey));
  useEffect(() => {
    const onChange = (e) => {
      if (e.detail?.moduleKey === moduleKey) setScopeState(e.detail.scope);
    };
    window.addEventListener("noam:context-changed", onChange);
    return () => window.removeEventListener("noam:context-changed", onChange);
  }, [moduleKey]);
  const setScope = useCallback((next) => {
    setScopeState(next);
    writeScope(moduleKey, next);
    window.dispatchEvent(new CustomEvent("noam:context-changed", { detail: { moduleKey, scope: next } }));
  }, [moduleKey]);
  return [scope, setScope];
}

/**
 * Serialize the scope into a short string that can be appended to NOAM prompts
 * — used by AI-calling endpoints when they accept a `context_hint` field.
 */
export function describeScope(scope) {
  if (!scope || !scope.type) return "Mon organisation";
  if (scope.type === "self") return "Mon profil personnel";
  if (scope.type === "org") return "Mon organisation";
  if (scope.type === "project") return `Projet : ${scope.label || scope.id || "—"}`;
  if (scope.type === "external") return `Organisation externe : ${scope.name || "—"}`;
  return "Mon organisation";
}
